/* @jsx h */
import { defineComponent, h } from "vue";
import { CreateCustomerAddressData } from "@lemmonhq/types";

export interface CustomerAddressManualFormProps {
  form: CreateCustomerAddressData;
  onUpdate: (field: keyof CreateCustomerAddressData, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  errorMessage: string;
  onClearCoordinates: () => void;
}

export const CustomerAddressManualForm =
  defineComponent<CustomerAddressManualFormProps>({
    name: "CustomerAddressManualForm",
    props: [
      "form",
      "onUpdate",
      "onSubmit",
      "onCancel",
      "isSubmitting",
      "errorMessage",
      "onClearCoordinates",
    ],
    setup(props) {
      const updateField = (
        field: keyof CreateCustomerAddressData,
        event: Event,
      ) => {
        props.onUpdate(field, (event.target as HTMLInputElement).value);
      };

      return () => (
        <div class="space-y-4">
          {props.errorMessage && (
            <div class="alert alert-error">
              <span class="material-symbols-outlined">error</span>
              {props.errorMessage}
            </div>
          )}

          <div class="grid gap-4 md:grid-cols-2">
            <label class="form-control">
              <span class="label-text font-medium">First name *</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="First name"
                value={props.form.first_name}
                onInput={(event) => updateField("first_name", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Last name *</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="Last name"
                value={props.form.last_name}
                onInput={(event) => updateField("last_name", event)}
              />
            </label>
            <label class="form-control md:col-span-2">
              <span class="label-text font-medium">Street *</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="Street, number, floor"
                value={props.form.street}
                onInput={(event) => updateField("street", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Postal code *</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="Postal code"
                value={props.form.postal_code}
                onInput={(event) => updateField("postal_code", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">City *</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="City"
                value={props.form.city}
                onInput={(event) => updateField("city", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Email *</span>
              <input
                type="email"
                class="input input-bordered"
                placeholder="Contact email"
                value={props.form.email}
                onInput={(event) => updateField("email", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Phone *</span>
              <input
                type="tel"
                class="input input-bordered"
                placeholder="Contact phone"
                value={props.form.phone}
                onInput={(event) => updateField("phone", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Company (optional)</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="Company name"
                value={props.form.company_name || ""}
                onInput={(event) => updateField("company_name", event)}
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">VAT / Registration</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="CH-123.456.789"
                value={props.form.company_registration || ""}
                onInput={(event) => updateField("company_registration", event)}
              />
            </label>
            <label class="form-control md:col-span-2">
              <span class="label-text font-medium">Notes</span>
              <textarea
                class="textarea textarea-bordered"
                rows={3}
                placeholder="Delivery instructions, gate code, etc."
                value={props.form.notes || ""}
                onInput={(event) =>
                  props.onUpdate(
                    "notes",
                    (event.target as HTMLTextAreaElement).value,
                  )
                }
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Latitude</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="Optional latitude"
                value={props.form.latitude || ""}
                onInput={(event) =>
                  props.onUpdate(
                    "latitude",
                    (event.target as HTMLInputElement).value,
                  )
                }
              />
            </label>
            <label class="form-control">
              <span class="label-text font-medium">Longitude</span>
              <input
                type="text"
                class="input input-bordered"
                placeholder="Optional longitude"
                value={props.form.longitude || ""}
                onInput={(event) =>
                  props.onUpdate(
                    "longitude",
                    (event.target as HTMLInputElement).value,
                  )
                }
              />
            </label>
          </div>

          {(props.form.latitude || props.form.longitude) && (
            <div class="alert alert-info flex items-center justify-between">
              <div>
                <span class="font-semibold">Geocoded position</span>
                <div class="text-sm opacity-80">
                  Lat {props.form.latitude || "?"}, Lng{" "}
                  {props.form.longitude || "?"}
                </div>
              </div>
              <button
                class="btn btn-sm btn-ghost"
                type="button"
                onClick={props.onClearCoordinates}
              >
                Clear
              </button>
            </div>
          )}

          <div class="flex justify-end gap-3">
            <button
              class="btn btn-ghost"
              onClick={props.onCancel}
              disabled={props.isSubmitting}
            >
              Cancel
            </button>
            <button
              class={`btn btn-primary ${props.isSubmitting ? "" : ""}`}
              onClick={props.onSubmit}
              disabled={props.isSubmitting}
            >
              {props.isSubmitting && (
                <span class="loading loading-spinner loading-sm" />
              )}
              Save address
            </button>
          </div>
        </div>
      );
    },
  });
